import { useState } from 'react';
import { ChevronRight, Users } from 'lucide-react';
import { memberRoleLabel, oldAppStillOffers } from '@mustauto/types/permissions';
import { maskPhone } from '@mustauto/utils';
import { Card, CardContent, EmptyState, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { SidePanel } from '@/components/ui/side-panel';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { Avatar, PersonSheet } from './PersonSheet';

/**
 * Equipe → Pessoas: uma linha por pessoa, com o papel NESTA oficina (não o
 * perfil da conta), "N ajustes" quando ela difere do papel, e o telefone só
 * para quem pode mudar a equipe. A ficha abre ao lado.
 */
export function PeopleTab() {
  const { data, isLoading, isError, error, refetch } = useTeamMembers();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (isError && !data) {
    return <ErrorState title="Não foi possível carregar a equipe" error={error} onRetry={() => refetch()} />;
  }
  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const { members, viewer } = data;
  // A ficha segue a lista: depois de salvar, a lista recarrega e a ficha mostra o novo.
  const selected = members.find((m) => m.userId === selectedId) ?? null;

  if (members.length <= 1) {
    return (
      <Card>
        <CardContent>
          <EmptyState
            icon={<Users size={28} />}
            title="Você trabalha sozinho por enquanto"
            description="Convide quem trabalha com você pelo botão Convidar. Cada pessoa entra com um papel, e você ajusta o que ela faz depois."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="divide-y divide-border p-0">
          {members.map((m) => {
            const adjustments = Object.keys(m.overrides).length;
            // Só quando o app antigo engana sobre algo: app desatualizado de
            // quem não perdeu nada fica na ficha, sem alarde na lista.
            const misleadingApp = m.outdatedApp && oldAppStillOffers(m).length > 0;
            const badges = (
              <>
                {misleadingApp && <Badge variant="destructive">app desatualizado</Badge>}
                {adjustments > 0 && (
                  <Badge variant="warning">
                    {adjustments} {adjustments === 1 ? 'ajuste' : 'ajustes'}
                  </Badge>
                )}
                <Badge variant={m.isOwner ? 'default' : 'secondary'}>{memberRoleLabel(m.role, m.isOwner)}</Badge>
              </>
            );
            return (
              <button
                key={m.userId}
                type="button"
                onClick={() => setSelectedId(m.userId)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
              >
                <Avatar member={m} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {m.name}
                    {m.userId === viewer.userId && <span className="ml-1 text-xs text-muted-foreground">(você)</span>}
                  </p>
                  {m.phone && <p className="whitespace-nowrap text-xs text-muted-foreground">{maskPhone(m.phone)}</p>}
                  {/* No celular os selos descem para baixo do nome. */}
                  <div className="mt-1 flex flex-wrap gap-1 sm:hidden">{badges}</div>
                </div>
                <div className="hidden shrink-0 items-center gap-2 sm:flex">{badges}</div>
                <ChevronRight size={16} className="shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </CardContent>
      </Card>

      <SidePanel open={selected !== null} onClose={() => setSelectedId(null)} title="Ficha da pessoa">
        {selected && <PersonSheet key={selected.userId} member={selected} viewer={viewer} />}
      </SidePanel>
    </>
  );
}
